import { IonContent, IonPage } from '@ionic/react';
import { useParams, useHistory } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth/useAuth';
import { getPublicProfile, profileInitials, profileDisplayName, stringToColor } from '@/lib/api/profiles';
import { getPublicationsByAuthor } from '@/lib/api/publications';
import { Logo } from '@/components/ui/Logo';

const KIND_LABEL: Record<string, string> = {
  article: 'Article', essay: 'Essay', book: 'Book', series: 'Series',
};

export default function ProfilePage() {
  const { handle } = useParams<{ handle: string }>();
  const history = useHistory();
  const { profile: myProfile } = useAuth();
  const isOwnProfile = myProfile?.handle === handle;

  const { data: profile, isLoading: profileLoading, isError } = useQuery({
    queryKey: ['profile', handle],
    queryFn: () => getPublicProfile(handle),
  });

  const { data: publications = [], isLoading: pubsLoading } = useQuery({
    queryKey: ['publications', profile?.id],
    queryFn: () => getPublicationsByAuthor(profile!.id),
    enabled: !!profile?.id,
  });

  return (
    <IonPage>
      <IonContent scrollY fullscreen>
        {/* Top bar */}
        <header className="sticky top-0 z-10 border-b border-gray-200 bg-white">
          <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3">
            <button
              onClick={() => history.push('/')}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Back"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <Logo size="sm" />
          </div>
        </header>

        <main className="mx-auto max-w-3xl px-5 py-10 pb-20">
          {profileLoading && (
            <div className="flex justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
            </div>
          )}

          {isError && (
            <div className="py-20 text-center text-gray-500">Profile not found.</div>
          )}

          {profile && (
            <>
              {/* Profile header */}
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div
                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full text-xl font-bold text-white"
                    style={{ background: stringToColor(profile.id) }}
                  >
                    {profileInitials(profile)}
                  </div>
                  <div>
                    <h1 className="font-serif text-2xl font-bold text-gray-900">
                      {profileDisplayName(profile)}
                    </h1>
                    <p className="text-sm text-gray-500">@{profile.handle}</p>
                  </div>
                </div>

                {isOwnProfile && (
                  <div className="flex shrink-0 gap-2">
                    <button
                      onClick={() => history.push('/publications/new')}
                      className="rounded-lg bg-[#2f3e46] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1e2c33]"
                    >
                      New publication
                    </button>
                    <button
                      onClick={() => history.push('/settings/profile')}
                      className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      Edit profile
                    </button>
                  </div>
                )}
              </div>

              {profile.bio && (
                <p className="mt-5 max-w-xl text-[0.95rem] leading-relaxed text-gray-700">
                  {profile.bio}
                </p>
              )}

              <p className="mt-2 text-xs text-gray-400">
                Joined {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>

              {/* Publications */}
              <div className="mt-10">
                <h2 className="mb-5 text-xs font-extrabold uppercase tracking-widest text-gray-400">
                  Publications
                </h2>

                {pubsLoading && (
                  <div className="flex justify-center py-8">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-[#2f3e46]" />
                  </div>
                )}

                {!pubsLoading && publications.length === 0 && (
                  <p className="py-8 text-center text-sm text-gray-400">
                    {isOwnProfile ? 'You haven\'t published anything yet.' : 'No publications yet.'}
                  </p>
                )}

                <div className="flex flex-col divide-y divide-gray-100">
                  {publications.map(pub => (
                    <div
                      key={pub.id}
                      className="group cursor-pointer py-4"
                      onClick={() => history.push(`/read`)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex flex-col gap-1">
                          <span className="text-[0.67rem] font-extrabold uppercase tracking-widest text-[#1a56a0]">
                            {KIND_LABEL[pub.kind] ?? pub.kind}
                          </span>
                          <h3 className="font-serif text-lg font-bold leading-snug text-gray-900 transition-colors group-hover:text-[#1a56a0]">
                            {pub.title}
                          </h3>
                          <p className="text-xs text-gray-400">
                            {new Date(pub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            {pub.visibility !== 'public' && (
                              <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-[0.65rem] font-medium text-gray-500">
                                {pub.visibility}
                              </span>
                            )}
                          </p>
                        </div>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" className="mt-1.5 shrink-0 transition-colors group-hover:stroke-[#1a56a0]">
                          <path d="M9 18l6-6-6-6"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </main>
      </IonContent>
    </IonPage>
  );
}

